import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://arthroscopydoc.com';
const DEFAULT_TITLE = 'Dr. Sunil Apsingi | Senior Orthopedic, Arthroscopy & Joint Replacement Surgeon';
const DEFAULT_DESC = 'Dr. Sunil Apsingi (MS Ortho PGI Chandigarh) is a renowned Chief Arthroscopy & Joint Replacement Surgeon in HITEC City, Hyderabad with 23+ years of excellence in knee, shoulder, sports medicine, and robotic surgeries.';
const DEFAULT_IMAGE = `${SITE_URL}/images/2022-05-13.png`;
const DEFAULT_KEYWORDS = 'Dr Sunil Apsingi, Orthopedic Surgeon Hyderabad, Knee Replacement HITEC City, Shoulder Arthroscopy, Sports Medicine Hyderabad, ACL Reconstruction, Robotic Knee Replacement';

/**
 * Reusable SEO component for dynamic Client-Side Head management & JSON-LD injection
 */
const SEOHead = ({
  title,
  description = DEFAULT_DESC,
  canonicalPath = '',
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  keywords = DEFAULT_KEYWORDS,
  schemaJson = null,
  breadcrumbs = null,
  noindex = false,
}) => {
  // Format title
  const fullTitle = title
    ? (title.includes('Dr. Sunil Apsingi') ? title : `${title} | Dr. Sunil Apsingi`)
    : DEFAULT_TITLE;

  // Format canonical URL
  const canonicalUrl = canonicalPath.startsWith('http')
    ? canonicalPath
    : `${SITE_URL}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;

  // Format image URL
  const fullImageUrl = ogImage.startsWith('http')
    ? ogImage
    : `${SITE_URL}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`;

  // Generate breadcrumbs schema if provided
  let breadcrumbSchema = null;
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.path.startsWith('http') ? crumb.path : `${SITE_URL}${crumb.path.startsWith('/') ? crumb.path : `/${crumb.path}`}`,
      })),
    };
  }

  // Combine schemas
  const allSchemas = [];
  if (schemaJson) {
    if (Array.isArray(schemaJson)) {
      allSchemas.push(...schemaJson);
    } else {
      allSchemas.push(schemaJson);
    }
  }
  if (breadcrumbSchema) {
    allSchemas.push(breadcrumbSchema);
  }

  const robotsDirective = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

  return (
    <Helmet>
      {/* Standard HTML Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Dr. Sunil Apsingi" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Crawl Directives */}
      <meta name="robots" content={robotsDirective} />
      <meta name="googlebot" content={robotsDirective} />
      <meta name="bingbot" content={robotsDirective} />

      {/* Local SEO & Geo Targeting (Hyderabad, Telangana) */}
      <meta name="geo.region" content="IN-TG" />
      <meta name="geo.placename" content="HITEC City, Hyderabad" />
      <meta name="geo.position" content="17.4474;78.3762" />
      <meta name="ICBM" content="17.4474, 78.3762" />

      {/* OpenGraph / Facebook / WhatsApp */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Dr. Sunil Apsingi Orthopedics" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* Structured Data (JSON-LD) */}
      {allSchemas.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
